import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FiTrendingDown, FiTrendingUp, FiHeart, FiTarget, FiCalendar,
  FiVideo, FiCheck, FiStar, FiUsers, FiClock, FiShield, FiActivity, FiSun
} from 'react-icons/fi';
import TableOfContents from '../components/TableOfContents';

const Services = () => {
  const servicesSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'item': {
          '@type': 'Service',
          'name': 'Weight Loss Programs',
          'description': 'Sustainable weight loss through personalized nutrition plans and clinical lifestyle coaching.',
          'provider': {
            '@type': 'Organization',
            'name': 'Dfitclub',
            'url': 'https://dfitclub.com'
          }
        }
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'item': {
          '@type': 'Service',
          'name': 'Weight Gain Programs',
          'description': 'Structured nutrition plans to gain weight healthily with a muscle building focus.',
          'provider': {
            '@type': 'Organization',
            'name': 'Dfitclub',
            'url': 'https://dfitclub.com'
          }
        }
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'item': {
          '@type': 'Service',
          'name': 'Skin and Hair Toning Nutrition',
          'description': 'Nutrition-focused approach to achieve healthy, glowing skin and strong hair through dietary changes.',
          'provider': {
            '@type': 'Organization',
            'name': 'Dfitclub',
            'url': 'https://dfitclub.com'
          }
        }
      }
    ]
  };

  useSEO({
    title: 'Personalized Nutrition & Diet Programs | Services',
    description: 'Explore Dfitclub specialized nutrition programs: weight loss, weight gain, healthy wedding prep plans, and skin toning diets. Custom dietitian plans with dedicated support.',
    keywords: 'dietitian programs, weight loss plan, weight gain nutrition, glowing skin diet, pre-wedding diet, Dfitclub services',
    schema: servicesSchema
  });

  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1, triggerOnce: true });

  const tocSections = [
    { id: 'hero', title: 'Our Services' },
    { id: 'weight-loss', title: 'Weight Loss Programs' },
    { id: 'weight-gain', title: 'Weight Gain Programs' },
    { id: 'additional-services', title: 'Additional Services' },
    { id: 'why-choose-us', title: 'Why Choose Us' }
  ];

  const services = [
    {
      icon: FiTrendingDown,
      title: 'Weight Loss Programs',
      subtitle: 'Most Popular',
      description: 'Sustainable weight loss through personalized nutrition plans and lifestyle coaching',
      features: [
        'Personalized diet plans on Android/iOS app',
        'Custom meal plans based on your preferences',
        'Weekly progress tracking and adjustments',
        'Custom made recipe access',
        'Easy to follow homemade diet plans',
        'WhatsApp support and motivation',
        'Behavioral counseling for lasting habits',
        'Exercise recommendations (optional)'
      ],
      duration: '3-12 months',
      // price: 'Starting from ₹5,000/month',
      highlight: true
    },
    {
      icon: FiTrendingUp,
      title: 'Weight Gain Programs',
      subtitle: 'Healthy Growth',
      description: 'Structured nutrition plans to gain weight healthily with muscle building focus',
      features: [
        'Personalized diet plans on Android/iOS app',
        'High-calorie meal plans with balanced nutrition',
        'Protein-rich recipes and supplements guidance',
        'Muscle building nutrition strategies',
        'Regular weight and measurement tracking',
        'Digestive health optimization',
        'Lifestyle and sleep guidance'
      ],
      duration: '4-8 months',
      // price: 'Starting from ₹4,500/month',
      highlight: false
    },
    {
      icon: FiActivity,
      title: 'Disease Management',
      subtitle: 'PCOS/PCOD, Thyroid, Diabetes',
      description: 'Specialized nutrition plans for managing chronic conditions and improving overall health',
      features: [
        'Condition-specific meal plans and guidance',
        'Blood sugar and hormone balancing nutrition',
        'Analysis of medical reports and history',
        'Anti-inflammatory diet plans',
        'Regular health parameter monitoring',
        'Supplement and medication coordination',
        'Lifestyle modifications for optimal health'
      ],
      duration: '6-12 months',
      // price: 'Starting from ₹5,500/month',
      highlight: false
    },
    {
      icon: FiSun,
      title: 'Skin and Hair Toning',
      subtitle: 'Glow from Within',
      description: 'Nutrition-focused approach to achieve healthy, glowing skin through dietary changes',
      features: [
        'Skin-specific nutrient-rich meal plans',
        'Hydration and detox plans',
        'Anti-aging and collagen-boosting foods',
        'Hair Conditioning nutritional plans',
        'Gut health optimization for clear skin',
        'Supplement guidance for skin health',
        'Lifestyle tips for radiant complexion'
      ],
      duration: '3-6 months',
      // price: 'Starting from ₹4,000/month',
      highlight: false
    },
    {
      icon: FiHeart,
      title: 'Wedding Plans',
      subtitle: 'Pre-Wedding Nutrition & Glow-Up',
      description: 'Holistic nutrition programs designed to help brides, grooms, and couples look and feel their best on the big day.',
      features: [
        'Goal-oriented meal plans for fat loss or toning',
        'Skin-brightening and hair-nourishing diet plans',
        'Detox and hydration routines pre-event',
        'Stress-reducing foods and supplements',
        'Flexible timeline planning leading up to the wedding',
        'Outfit-ready shape tracking and adjustments'
      ],
      duration: '3-6 months',
      // price: 'Starting from ₹3,500/month',
      highlight: false
    }
  ];

  const additionalServices = [
    {
      icon: FiTarget,
      title: 'Customized Diet Plans',
      description: 'Tailored nutrition plans for specific health conditions, food allergies, or dietary preferences',
      // price: '₹2,500 - ₹4,000'
    },
    {
      icon: FiCalendar,
      title: 'Weekly Check-ins',
      description: 'Regular monitoring sessions to track progress and make necessary adjustments',
      // price: '₹1,500/month'
    },
    {
      icon: FiVideo,
      title: 'Group Workshops',
      description: 'Educational sessions on nutrition, meal prep, and healthy cooking techniques',
      // price: '₹500/session'
    }
  ];

  const whyChooseUs = [
    {
      icon: FiStar,
      title: 'Proven Results',
      description: '95% success rate with over 500+ successful transformations'
    },
    {
      icon: FiUsers,
      title: 'Personalized Approach',
      description: 'Every plan is customized to your lifestyle, preferences, and goals'
    },
    {
      icon: FiClock,
      title: 'Ongoing Support',
      description: 'WhatsApp support and regular check-ins throughout your journey'
    },
    {
      icon: FiShield,
      title: 'Science-Based',
      description: 'Evidence-based nutrition strategies backed by latest research'
    }
  ];

  return (
    <div className="min-h-screen">
      <TableOfContents sections={tocSections} />
      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="section-padding bg-gradient-to-br from-primary-50 to-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="heading-hero text-gray-900 mb-6">
              Our <span className="text-visible-orange">Services</span>
            </h1>
            <p className="text-section-body">
              Comprehensive health and nutrition programs designed to help you achieve your goals sustainably
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section id="services-main" ref={servicesRef} className="section-padding bg-white">
        <div id="weight-loss"></div>
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={servicesInView ? {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  rotateY: [0, 5, 0],
                  rotateX: [0, -2, 0]
                } : {}}
                transition={{
                  duration: 0.8,
                  delay: index * 0.15,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  rotateY: 8,
                  rotateX: -3,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                className={`service-card group relative overflow-hidden h-full flex flex-col ${service.highlight ? 'ring-2 ring-primary-500' : ''
                  }`}
              >
                {/* Floating animation background */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-primary-50 to-secondary-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  animate={{
                    y: [0, -5, 0],
                    x: [0, 3, 0]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: index * 0.5,
                    ease: "easeInOut"
                  }}
                />

                {/* Glow effect */}
                <motion.div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.1) 0%, transparent 70%)`
                  }}
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />

                {service.highlight && (
                  <motion.div
                    className="absolute top-0 left-0 right-0 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-center py-2 text-sm font-medium z-20"
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.2 + 0.3 }}
                  >
                    {service.subtitle}
                  </motion.div>
                )}

                <div className={`p-4 ${service.highlight ? 'pt-12' : ''} relative z-10 flex flex-col h-full`}>
                  {/* Icon with enhanced animation */}
                  <motion.div
                    className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-3 relative z-10"
                    whileHover={{
                      scale: 1.1,
                      rotate: 5,
                      transition: { duration: 0.3 }
                    }}
                    animate={{
                      y: [0, -3, 0],
                      rotate: [0, 1, 0]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: index * 0.3,
                      ease: "easeInOut"
                    }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-300 to-primary-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0, 0.3, 0]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                    <service.icon className="w-6 h-6 text-primary-600 relative z-10" />
                  </motion.div>

                  {/* Content with staggered animation */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.2 + 0.3 }}
                    className="flex flex-col flex-grow"
                  >
                    <h3 className="text-lg font-bold text-secondary-900 mb-1 text-center group-hover:text-primary-700 transition-colors duration-300">
                      {service.title}
                    </h3>
                    {!service.highlight && (
                      <p className="text-primary-600 text-center mb-2 font-medium text-sm">{service.subtitle}</p>
                    )}

                    <p className="text-secondary-600 text-center mb-3 group-hover:text-secondary-700 transition-colors duration-300 text-sm">
                      {service.description}
                    </p>

                    {/* Features with staggered animation */}
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          className="flex items-start space-x-3"
                          initial={{ opacity: 0, x: -10 }}
                          animate={servicesInView ? { opacity: 1, x: 0 } : {}}
                          transition={{
                            duration: 0.4,
                            delay: index * 0.2 + 0.4 + featureIndex * 0.1
                          }}
                          whileHover={{ x: 5, transition: { duration: 0.2 } }}
                        >
                          <motion.div
                            whileHover={{ scale: 1.2, rotate: 5 }}
                            transition={{ duration: 0.2 }}
                          >
                            <FiCheck className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                          </motion.div>
                          <span className="text-secondary-700 text-sm group-hover:text-secondary-800 transition-colors duration-300">
                            {feature}
                          </span>
                        </motion.li>
                      ))}
                    </ul>

                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between items-center">
                        <span className="text-secondary-600">Duration:</span>
                        <span className="font-medium text-secondary-900">{service.duration}</span>
                      </div>
                    </div>

                    {/* Spacer to push buttons to bottom */}
                    <div className="flex-grow"></div>

                    {/* Enhanced buttons */}
                    {service.title === 'Disease Management' ? (
                      <div className="space-y-3 relative z-20">
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Link
                            to="/contact"
                            className="w-full btn-primary text-center block"
                          >
                            Get Started
                          </Link>
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Link
                            to="/disease-management"
                            className="w-full btn-secondary text-center block"
                          >
                            Learn More About Conditions
                          </Link>
                        </motion.div>
                      </div>
                    ) : (
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="relative z-20"
                      >
                        <Link
                          to="/contact"
                          className="w-full btn-primary text-center block"
                        >
                          Get Started
                        </Link>
                      </motion.div>
                    )}
                  </motion.div>
                </div>

                {/* Particle effects */}
                <motion.div
                  className="absolute top-4 right-4 w-2 h-2 bg-primary-400 rounded-full opacity-0 group-hover:opacity-100"
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    y: [0, -20, -40]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 0.5
                  }}
                />
                <motion.div
                  className="absolute bottom-4 left-4 w-1 h-1 bg-secondary-400 rounded-full opacity-0 group-hover:opacity-100"
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    x: [0, 15, 30]
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    delay: 1
                  }}
                />
                <Link
                  to={service.title === 'Disease Management' ? '/disease-management' : '/contact'}
                  className="absolute inset-0 z-10"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="heading-section text-gray-900 mb-4">
              Additional <span className="text-visible-orange">Services</span>
            </h2>
            <p className="text-section-body">
              Complementary services to support your health journey
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="card p-4 text-center card-hover"
              >
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <service.icon className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="text-base font-semibold text-secondary-900 mb-2">{service.title}</h3>
                <p className="text-secondary-600 mb-3 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="heading-section text-gray-900 mb-4">
              Why Choose <span className="text-visible-orange">DFITCLUB</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-3">{item.title}</h3>
                <p className="text-secondary-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-100 to-primary-200 text-neutral-800">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="heading-section text-gray-900 mb-4">
              Ready to Transform Your Health?
            </h2>
            <p className="text-section-body mb-6">
              Book your free consultation today and take the first step towards a healthier you
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-accent">
                Enroll Now
              </Link>
              <Link to="/transformations" className="btn-secondary">
                See Success Stories
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;